import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as hrmsService from "../../services/hrmsService";
import AppDropdown from "../../components/AppDropdown/AppDropdown";
import { HR_PERMISSIONS, hasHrPermission } from "../../constants/hrPermissions";
import EmployeeAddDrawer from "./EmployeeAddDrawer";
import EmployeeImportDrawer from "./EmployeeImportDrawer";
import { EmptyState, LoadState, PageHeader, PrimaryButton, SecondaryButton } from "./hrComponents";
import { useHr } from "./HrGate";
import { useMasterOptions } from "./hrForm";
import { cardStyle, formatDate, inputStyle, pageStyle, statusBadge, statusLabel, tdStyle, thStyle, useLoad } from "./hrUi";

const PAGE_SIZE = 25;
const STATUS_OPTIONS = [
  { label: "Working (not left)", value: "WORKING" },
  { label: "Active", value: "ACTIVE" },
  { label: "On notice", value: "ON_NOTICE" },
  { label: "Suspended", value: "SUSPENDED" },
  { label: "Left the company", value: "SEPARATED" },
  { label: "Everyone", value: "ALL" },
];

// EMP-*: people the caller may see (everyone for HR, their own reports for a manager).
export default function Employees() {
  const navigate = useNavigate();
  const { access } = useHr();
  // The Employees tier (plans other than the HR plan): the same list, without the parts that need the full module.
  const lite = !access.enabled;
  const isAdmin = lite ? !!access.is_tenant_admin || (access.permissions || []).includes(HR_PERMISSIONS.HR_ADMIN) : hasHrPermission(access, HR_PERMISSIONS.HR_ADMIN);
  const base = lite ? "/hr/team" : "/hr/employees";
  const isHr = hasHrPermission(access, HR_PERMISSIONS.HR_ADMIN, HR_PERMISSIONS.HR_VIEWER);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("WORKING");
  const [department, setDepartment] = useState("ALL");
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const options = useMasterOptions(["departments"]);

  // Search as you type, but not on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(q.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const { data, loading, error, reload } = useLoad(
    () => hrmsService.listEmployees({ q: search, status: status === "WORKING" ? undefined : status, department_id: department === "ALL" ? undefined : department, page, size: PAGE_SIZE }),
    [search, status, department, page]
  );
  const filtered = !!search || status !== "WORKING" || department !== "ALL";
  const rows = data?.rows || [];
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div style={pageStyle}>
      <PageHeader
        title={isHr || lite ? "Employees" : "My Team"}
        subtitle={data ? `${total} ${total === 1 ? "person" : "people"}` : undefined}
        actions={
          isAdmin ? (
            <>
              {lite ? null : <SecondaryButton onClick={() => setImporting(true)}>Import</SecondaryButton>}
              <PrimaryButton onClick={() => setAdding(true)}>Add employee</PrimaryButton>
            </>
          ) : null
        }
      />

      <div className="d-flex flex-wrap gap-2 mb-3">
        <input type="search" className="form-control" style={{ ...inputStyle, maxWidth: 280 }} placeholder="Search name, code or email" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search employees" />
        <div style={{ width: 200 }}>
          <AppDropdown value={status} options={STATUS_OPTIONS} onChange={(e) => { setStatus(e.value); setPage(1); }} />
        </div>
        <div style={{ width: 200 }}>
          <AppDropdown
            value={department}
            options={[{ label: "All departments", value: "ALL" }, ...(options.departments || [])]}
            onChange={(e) => { setDepartment(e.value); setPage(1); }}
          />
        </div>
      </div>

      <LoadState loading={loading} error={error} onRetry={reload} rows={6}>
        {rows.length === 0 ? (
          <EmptyState
            title="No employees found"
            text={filtered ? "Try a different search or filter." : isAdmin ? "Add your first employee, or import a spreadsheet." : undefined}
            action={isAdmin && !filtered ? <PrimaryButton onClick={() => setAdding(true)}>Add employee</PrimaryButton> : null}
          />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 820 }}>
              <thead>
                <tr>
                  {["Code", "Name", "Department", "Designation", "Location", "Reports to", "Joined", "Status"].filter((h) => !(lite && h === "Reports to")).map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.employee_id} onClick={() => navigate(`${base}/${e.employee_id}`)} style={{ cursor: "pointer" }}>
                    <td style={tdStyle}>{e.employee_code}</td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>{e.full_name}</div>
                      <div style={{ fontSize: 12, color: "#6B7280" }}>{e.work_email}</div>
                    </td>
                    <td style={tdStyle}>{e.department_name || "—"}</td>
                    <td style={tdStyle}>{e.designation_name || "—"}</td>
                    <td style={tdStyle}>{e.location_name || "—"}</td>
                    {lite ? null : <td style={tdStyle}>{e.manager_name || "—"}</td>}
                    <td style={tdStyle}>{formatDate(e.date_of_joining)}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(e.status)}>{statusLabel(e.status)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>

      {pages > 1 ? (
        <div className="d-flex align-items-center justify-content-end gap-2 mt-3">
          <button type="button" className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Previous
          </button>
          <span style={{ fontSize: 13 }}>
            Page {page} of {pages}
          </span>
          <button type="button" className="btn btn-outline-secondary btn-sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
            Next
          </button>
        </div>
      ) : null}

      <EmployeeAddDrawer show={adding} onHide={() => setAdding(false)} onDone={reload} lite={lite} canMakeAdmin={!!access.is_tenant_admin} />
      {lite ? null : <EmployeeImportDrawer show={importing} onHide={() => setImporting(false)} onDone={reload} />}
    </div>
  );
}
